const COMPANY_WIDE_ROLES = ['company_admin', 'pecsmart_admin']
const FEED_ONLY_ROLES = ['farm_admin', 'farm_employee']

function asObject(value) {
  if (!value) return null
  if (typeof value === 'string') {
    try {
      return asObject(JSON.parse(value))
    } catch (error) {
      return null
    }
  }
  if (typeof value === 'object' && !Array.isArray(value)) return value
  return null
}

function idFromText(text) {
  const path = String(text).split(/[?#]/)[0]
  const parts = path.split('/').filter(Boolean)
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    if (!/^\d+$/.test(parts[index])) continue
    const pk = Number(parts[index])
    if (pk > 0) return pk
  }
  return null
}

function clientFrontend(companySettings) {
  const settings = asObject(companySettings?.settings) || {}
  const client = asObject(settings.client_frontend) || {}
  const merged = { ...settings, ...client }
  const farmPks = uniquePks([settings.feed_inventory_farm_pks, client.feed_inventory_farm_pks])
  if (farmPks.length) merged.feed_inventory_farm_pks = farmPks
  const userFarms = mergeUserFarmMaps(settings.feed_inventory_user_farms, client.feed_inventory_user_farms)
  if (Object.keys(userFarms).length) merged.feed_inventory_user_farms = userFarms
  return merged
}

function associatedFarmPk(user) {
  return uniquePks(user?.associated_farm)[0] ?? null
}

/**
 * O id usado em barns/report/home/farm= é farm.farm (ou o último segmento da URL).
 * farm.pk pode ser outro registro; api1 no host não pode virar pk 1.
 * A ordem devolve o id canônico primeiro e os demais aliases depois.
 */
export function farmIds(entry, seen = new Set()) {
  if (entry == null || entry === '') return []
  if (typeof entry === 'number') return Number.isFinite(entry) && entry > 0 ? [entry] : []
  if (typeof entry === 'string') {
    const trimmed = entry.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return farmIds(JSON.parse(trimmed), seen)
      } catch (error) {
        /* segue para url ou lista */
      }
    }
    const splitList = trimmed.includes('://') ? null : trimmed.split(/[,;\s]+/)
    if (splitList && splitList.length > 1) return splitList.flatMap((part) => farmIds(part, seen))
    const pk = /^\d+$/.test(trimmed) ? Number(trimmed) : idFromText(trimmed)
    return pk > 0 ? [pk] : []
  }
  if (Array.isArray(entry)) return entry.flatMap((item) => farmIds(item, seen))
  if (typeof entry !== 'object') return []
  if (seen.has(entry)) return []
  seen.add(entry)

  const ids = []
  const push = (value) => {
    farmIds(value, seen).forEach((pk) => {
      if (!ids.includes(pk)) ids.push(pk)
    })
  }
  if (entry.farm && typeof entry.farm === 'object') {
    push(entry.farm.pk ?? entry.farm.id)
    push(entry.farm.url)
  } else {
    push(entry.farm)
  }
  push(entry.farm_pk)
  push(entry.pk)
  push(entry.id)
  push(entry.url)
  push(entry.farms)
  push(entry.farm_pks)
  push(entry.pks)
  return ids
}

function pkList(raw) {
  return farmIds(raw)
}

function farmRecord(entry) {
  const aliases = farmIds(entry)
  if (!aliases.length) return null
  const name = entry && typeof entry === 'object'
    ? (entry.name || entry.farm_name || entry.farm?.name || '')
    : ''
  return { pk: aliases[0], name, aliases }
}

/** Pede o id canônico de cada granja configurada, inclusive quando o pk do objeto é outro. */
export function selectRequestedFarms(companyFarms, limit) {
  const known = (Array.isArray(companyFarms) ? companyFarms : []).map(farmRecord).filter(Boolean)
  const wantedIds = limit == null ? null : farmIds(limit)
  if (!wantedIds) return known
  const wanted = new Set(wantedIds.map((pk) => String(pk)))
  const covered = new Set()
  const picked = []
  known.forEach((farm) => {
    if (!farm.aliases.some((pk) => wanted.has(String(pk)))) return
    if (covered.has(String(farm.pk))) return
    farm.aliases.forEach((pk) => covered.add(String(pk)))
    covered.add(String(farm.pk))
    picked.push(farm)
  })
  wantedIds.forEach((pk) => {
    if (covered.has(String(pk))) return
    covered.add(String(pk))
    picked.push({ pk, name: '', aliases: [pk] })
  })
  return picked
}

function uniquePks(raw) {
  const seen = new Set()
  const pks = []
  pkList(raw).forEach((pk) => {
    if (seen.has(pk)) return
    seen.add(pk)
    pks.push(pk)
  })
  return pks
}

function accountNames(user) {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('username') : null
  return [user?.username, user?.user?.username, user?.user, stored]
    .map((name) => String(name || '').trim().toLowerCase())
    .filter(Boolean)
}

function asUserFarmMap(raw) {
  let map = raw
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map)
    } catch (error) {
      return {}
    }
  }
  const out = {}
  const put = (name, value) => {
    const key = String(name || '').trim().toLowerCase()
    if (!key) return
    const pks = uniquePks([out[key], value])
    if (pks.length) out[key] = pks
  }
  if (Array.isArray(map)) {
    map.forEach((item) => {
      if (!item || typeof item !== 'object') return
      put(item.username || item.user, item.farms ?? item.farm_pks ?? item.pks ?? item)
    })
    return out
  }
  if (map && typeof map === 'object') {
    Object.keys(map).forEach((key) => put(key, map[key]))
  }
  return out
}

function mergeUserFarmMaps(rootMap, clientMap) {
  const root = asUserFarmMap(rootMap)
  const client = asUserFarmMap(clientMap)
  const merged = {}
  new Set([...Object.keys(root), ...Object.keys(client)]).forEach((name) => {
    const pks = uniquePks([client[name], root[name]])
    if (pks.length) merged[name] = pks
  })
  return merged
}

function userFarmPks(client, user) {
  const map = asUserFarmMap(client.feed_inventory_user_farms)
  const names = accountNames(user)
  const key = Object.keys(map).find((item) => names.includes(item))
  if (key) return map[key]

  const farmPk = associatedFarmPk(user)
  if (farmPk == null) return null
  const lists = Object.values(map).filter((pks) => pks.includes(farmPk))
  return lists.length === 1 ? lists[0] : null
}

/**
 * client_frontend.feed_inventory ligado
 *   company_admin e pecsmart_admin veem todas as granjas.
 * client_frontend.feed_inventory_farm_pks: [232, 203]
 *   só vale com feed_inventory ligado. Quem tem a granja associada
 *   nessa lista vê estoque, consumo e rota de todas essas granjas.
 * client_frontend.feed_inventory_only
 *   com feed_inventory ligado e a granja na lista, farm_admin
 *   e farm_employee entram direto no estoque e não veem outros sensores.
 * client_frontend.feed_inventory_user_farms: { "pacuca": [232, 203] }
 *   com feed_inventory ligado, esse usuário vê estoque, consumo e rota
 *   só dessas granjas.
 */
export function resolveFeedInventoryAccess(user, companySettings) {
  const denied = { allowed: false, farmPk: null, farmPks: null }
  const role = user?.role
  if (!role) return denied

  const client = clientFrontend(companySettings)
  if (!client.feed_inventory) return denied

  const scopedFarms = userFarmPks(client, user)
  if (scopedFarms) {
    return {
      allowed: true,
      farmPk: scopedFarms.length === 1 ? scopedFarms[0] : null,
      farmPks: scopedFarms,
    }
  }

  if (COMPANY_WIDE_ROLES.includes(role)) {
    return { allowed: true, farmPk: null, farmPks: null }
  }

  const enabled = uniquePks(client.feed_inventory_farm_pks)
  const farmPk = associatedFarmPk(user)
  if (!enabled.length || farmPk == null || !enabled.includes(farmPk)) return denied
  return {
    allowed: true,
    farmPk: enabled.length === 1 ? enabled[0] : null,
    farmPks: enabled,
  }
}

export function isFeedInventoryOnly(user, companySettings) {
  if (!FEED_ONLY_ROLES.includes(user?.role)) return false
  if (!clientFrontend(companySettings).feed_inventory_only) return false
  return resolveFeedInventoryAccess(user, companySettings).allowed
}

/** Cam, mic e smartfeed não carregam para quem está só no estoque de ração. */
export function sensorScreenBlocked(user, companySettings) {
  return isFeedInventoryOnly(user, companySettings)
}
